// Index view: OP plus its last few replies, one row per thread.
//
// The catalog collapses a thread to a thumbnail; the index shows the thread
// the way it reads — the OP, then the tail of the conversation. Picking that
// tail is all this module does. Pure functions: no DOM, no network, no global
// state (tests/indexview.test.js).

import { effectiveStatus, resolveThreadRootId } from "./catalog.js";
import { buildReplyTree, flattenThreadChronological } from "./threading.js";

// How many replies sit under an OP in the index.
export const INDEX_REPLY_LIMIT = 5;
// A stickied thread is the author's notice board, not a conversation, and on a
// profile there can be several in a row — one reply each keeps them from
// pushing the actual posts off the screen.
export const INDEX_STICKY_REPLY_LIMIT = 1;

export function indexReplyLimit(isSticky) {
  return isSticky ? INDEX_STICKY_REPLY_LIMIT : INDEX_REPLY_LIMIT;
}

function countImageReplies(statuses) {
  let n = 0;
  for (const s of statuses) {
    if ((s?.media_attachments || []).length > 0) n += 1;
  }
  return n;
}

function tail(ordered, limit, { partial }) {
  const cap = Math.max(0, limit);
  const replies = cap > 0 ? ordered.slice(-cap) : [];
  const omittedPosts = ordered.slice(0, ordered.length - replies.length);
  return {
    replies,
    total: ordered.length,
    omitted: omittedPosts.length,
    omittedImages: countImageReplies(omittedPosts),
    partial: !!partial,
  };
}

// The last `limit` replies of a fully-loaded thread, from a /context payload's
// descendants. Ordered the way the thread view orders them, so the tail here
// is the tail the reader sees after clicking through.
export function selectIndexReplies(descendants, opId, limit) {
  const ordered = flattenThreadChronological(buildReplyTree(descendants || [], opId));
  return tail(ordered, limit, { partial: false });
}

// The same shape, built from the replies that happen to be in the loaded
// timeline page. Timelines come back newest-first, so whatever is here *is*
// the recent end of the thread — but a thread bumped by one reply contributes
// only that one, and /home/ never carries replies from accounts the reader
// doesn't follow. Hence `partial`: the counts are a floor, and the row is
// replaced once its context has been fetched.
export function seedIndexReplies(statuses, opId, limit) {
  if (!opId) return tail([], limit, { partial: true });

  const byId = new Map();
  for (const row of statuses || []) {
    const s = effectiveStatus(row);
    if (s?.id) byId.set(s.id, s);
  }

  const replies = [];
  for (const row of statuses || []) {
    // A repost is an activity on the thread, not a post in it.
    if (row?.reblog) continue;
    const s = effectiveStatus(row);
    if (!s?.id || s.id === opId || !s.in_reply_to_id) continue;
    if (resolveThreadRootId(s, byId) !== opId) continue;
    replies.push(s);
  }
  replies.sort((a, b) =>
    (a.created_at || "").localeCompare(b.created_at || "") || (a.id || "").localeCompare(b.id || ""));

  return tail(replies, limit, { partial: true });
}

// 4chan's summary line: "+2 posts and 2 image replies omitted." The image
// clause is dropped when none of the omitted posts had a file, and the whole
// line when nothing was omitted.
export function omittedLabel({ omitted = 0, omittedImages = 0 } = {}) {
  if (omitted <= 0) return "";
  const posts = `${omitted} post${omitted === 1 ? "" : "s"}`;
  if (omittedImages <= 0) return `+ ${posts} omitted.`;
  const images = `${omittedImages} image ${omittedImages === 1 ? "reply" : "replies"}`;
  return `+ ${posts} and ${images} omitted.`;
}
